import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "./button";
import { Select } from "./input";
import { chooseLocale, currentLocale, locales, notePicked, offeredLocales, type Locale } from "../locale";

export type LanguageSwitchProps = {
  /** Remember the choice beyond this browser (a signed-in person's profile) before the page reloads in it. */
  onChoose?: (locale: Locale) => Promise<unknown> | void;
  size?: "sm" | "md";
  className?: string;
};

/**
 * The languages a page can switch to and the act of switching: `others` is every offered language but the current one,
 * `choose` remembers the choice (and saves it through `onChoose`) and reloads the page in it. For a control of your own,
 * such as an item in a menu; `LanguageSwitch` is the ready-made button.
 */
export function useLanguageChoice(onChoose?: LanguageSwitchProps["onChoose"]) {
  const [busy, setBusy] = useState(false);
  const current = currentLocale();
  const others = offeredLocales().filter((l) => l !== current);

  async function choose(locale: Locale) {
    setBusy(true);
    try {
      await onChoose?.(locale);
    } finally {
      notePicked(locale);
      chooseLocale(locale);
    }
  }

  return { current, others, busy, choose };
}

/**
 * Switches the page's language. With two languages it is one quiet button naming the other in its own words
 * ("العربية" on an English page, "English" on an Arabic one); with more, a list. The page reloads in the new language.
 * Renders nothing where the page has one language.
 */
export function LanguageSwitch({ onChoose, size = "sm", className }: LanguageSwitchProps) {
  const { t } = useLingui();
  const { current, others, busy, choose } = useLanguageChoice(onChoose);
  if (others.length === 0) return null;

  if (others.length === 1) {
    const other = others[0]!;
    const name = locales[other].name;
    return (
      <Button variant="ghost" size={size} className={className} disabled={busy} onClick={() => void choose(other)} aria-label={t`Language: ${name}`}>
        <span lang={other} dir={locales[other].dir}>
          {name}
        </span>
      </Button>
    );
  }

  return (
    <Select aria-label={t`Language`} className={className} value={current} disabled={busy} onChange={(e) => void choose(e.target.value as Locale)}>
      {offeredLocales().map((l) => (
        <option key={l} value={l} lang={l}>
          {locales[l].name}
        </option>
      ))}
    </Select>
  );
}
